// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React, { type Node } from 'react';
import { uniqueId } from 'lodash';

import {
  Animation,
  Colors,
  Icon,
  type IconName,
  Link,
  Tooltip,
  Typography,
  createUniqueClassName,
} from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

/** @jsx isolateComponent */

export type Props = {|
  /**
   * The URL to where the link goes.
   */
  href: string,
  /**
   * The `Icon` component to render within the link.
   * **Note that if renderBeforeText is defined, that prop is then responsible for rendering.**
   */
  icon?: IconName,
  /**
   * Indicates whether the link is collapsed down to just an icon.
   */
  isCollapsed: boolean,
  /**
   * Indicates whether the link goes to a page outside of
   * the logged in app experience.
   */
  isExternal: boolean,
  /**
   * Indicates whether the link is the selected/current link.
   */
  isSelected: boolean,
  /**
   * Optional click handler for the link.
   */
  onClick?: (SyntheticMouseEvent<HTMLElement>) => void,
  /**
   * Renders content before the link text. E.g; an Icon or Avatar
   */
  renderBeforeText?: (props: Props) => Node,
  /**
   * The text content to render within the link.
   */
  text: string,
|};

const tooltipClassName = createUniqueClassName(__filename, 'tooltip');
const tooltipSelector = `.${tooltipClassName}`;
const externalIconClassName = createUniqueClassName(__filename, 'externalIcon');
const externalIconSelector = `.${externalIconClassName}`;

const Styles = labelStyles(__filename, {
  root: props => [
    // Rare specificity bump over `Link`.
    withModifier('', {
      ...Typography.base,
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '20px',
      color: Colors.grey700,
      display: 'flex',
      position: 'relative',
      minHeight: 50,
      padding: 10,
      borderRadius: 10,
      whiteSpace: 'nowrap',
      width: '100%',
      transition: Animation.transitionValue,

      '&:hover, &:focus': {
        color: Colors.black,
        backgroundColor: Colors.grey100,
      },
    }),
    props.isSelected &&
      withModifier('selected', {
        color: Colors.blue500,
        backgroundColor: Colors.blue100,
      }),
    props.isCollapsed &&
      withModifier('collapsed', {
        width: 50,
      }),
    {
      [tooltipSelector]: {
        opacity: 0,
        pointerEvents: 'none',
        transition: 'opacity 0.1s ease-in-out',
        '@media (hover: none)': {
          display: 'none',
        },
      },
      [`&:hover ${tooltipSelector}, &:focus ${tooltipSelector}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
    {
      [externalIconSelector]: {
        opacity: 0,
      },
      [`&:hover ${externalIconSelector}, &:focus ${externalIconSelector}`]: {
        opacity: 0.5,
      },
    },
  ],
  layout: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gap: 20,
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  text: {
    transition: Animation.transitionValue,
    '&[aria-hidden="true"]': {
      opacity: 0,
    },
  },
});

/**
 * Renders a link within the universal sidebar.
 */
const UniversalNavLink = (props: Props) => {
  const tooltip = props.isCollapsed && (
    <span className={tooltipClassName}>
      <Tooltip id={uniqueId('tooltip-')} position="east">
        {props.text}
      </Tooltip>
    </span>
  );
  const onClick = e => {
    // Chrome is the only browser that causes an element to
    // be in a `:focus` state when it's clicked. That `:focus`
    // state causes `Tooltip` to undesirably render. The user
    // already clicked, so they don't need to see the tooltip.
    // To hide the tooltip, `blur` the node to cause it to no
    // longer have `:focus`. Tabbing location is still preserved.
    e.currentTarget.blur();
    if (props.onClick) {
      props.onClick(e);
    }
  };
  return (
    <Link css={Styles.root(props)} href={props.href} onClick={onClick}>
      <span css={Styles.layout}>
        {props.renderBeforeText && props.renderBeforeText(props)}
        <span aria-hidden={props.isCollapsed} css={Styles.text}>
          {props.text}
        </span>
        {props.isExternal && !props.isCollapsed && (
          <span className={externalIconClassName}>
            <Icon isBlock={true} name="arrowOffsite" size={18} />
          </span>
        )}
      </span>
      {tooltip}
    </Link>
  );
};

UniversalNavLink.defaultProps = {
  isCollapsed: false,
  isExternal: false,
  isSelected: false,
  renderBeforeText: (props: Props) =>
    props.icon && <Icon isBlock={true} name={props.icon} size={30} />,
};

export default UniversalNavLink;
